// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonVariants } from "@rc/MobiusOverrides/Button/Button";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    copy = "copy",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    url = "url",
    imagePath = "imagePath",
    alignment = "alignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: ButtonVariants;
        [fields.url]: LinkFieldValue;
        [fields.imagePath]: string;
        [fields.alignment]: "left" | "right" | "middle";
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    copy?: TypographyProps;
    buttonContainer?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            border: 1px solid ${getColor("common.ui3")};

            &.left {
                align-items: flex-start;
            }
            &.right {
                align-items: flex-end;
            }
            &.middle {
                align-items: center;
            }
        `,
    },
    buttonContainer: {
        css: css`
            margin-top: auto;
            padding: 0 17px 17px 17px;
        `,
    },
    title: {
        size: 20,
        lineHeight: "32px",
        color: "text.main",
        css: css`
            margin-bottom: 8px;
            font-weight: 500;
            padding: 0 17px;
        `,
    },
    copy: {
        size: 16,
        lineHeight: "24px",
        weight: "normal",
        color: "common.ui5",
        css: css`
            margin-bottom: 16px;
            padding: 0 17px;
        `,
    },
};

const styles = infoCardStyles;

const InfoCard = ({ id, fields: { title, copy, buttonText, buttonStyle, url, imagePath, alignment } }: Props) => {
    const linkUrl = useGetLink(url);

    return (
        <StyledWrapper className={`info-card ${alignment}`} {...styles.container}>
            {imagePath && (
                <StyledWrapper
                    css={css`
                        width: 100%;
                        height: 172px;
                        margin-bottom: 16px;
                        background-image: url(${imagePath});
                        background-repeat: no-repeat;
                        background-size: contain;
                        background-position: center;
                    `}
                ></StyledWrapper>
            )}
            <Typography {...styles.title}>{title}</Typography>
            <Typography {...styles.copy}>{parse(copy, parserOptions)}</Typography>
            {buttonText && (
                <StyledWrapper {...styles.buttonContainer}>
                    <Button variant={buttonStyle} href={linkUrl?.url}>
                        {buttonText}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        displayName: "Info Card",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.imagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                extendedConfig: { height: 120 },
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.buttonStyle,
                editorTemplate: "DropDownField",
                defaultValue: "secondary",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                    { displayName: "Middle", value: "middle" },
                ],
            },
        ],
    },
};

export default widgetModule;
